/**
 * Conditional Select Component
 *
 * Shows or hides content based on the selected value of a dropdown.
 * Supports multiple conditionals on the same select element.
 *
 * Usage:
 * Add data attributes to your select element:
 * - data-conditional-select: true (enables the behaviour)
 * - data-conditional-target: "element-id" (ID of element to show/hide)
 * - data-conditional-value: "value" or "value1,value2" (select value(s) that trigger showing)
 *
 * For multiple conditionals on the same select:
 * - data-conditional-target2: "second-element-id"
 * - data-conditional-value2: "different-value"
 *
 * Example:
 * <select data-conditional-select="true"
 *         data-conditional-target="other-field"
 *         data-conditional-value="Other">
 *   <option>Mr</option>
 *   <option>Other</option>
 * </select>
 * <div id="other-field" hidden>...</div>
 */

export default function initConditionalSelect() {
    // Find all select dropdowns that have conditional behaviour
    document.querySelectorAll('[data-conditional-select]').forEach((selectElement) => {
        // Read configuration from data attributes
        const firstTargetId = selectElement.dataset.conditionalTarget;
        const firstTriggerValue = selectElement.dataset.conditionalValue;
        const secondTargetId = selectElement.dataset.conditionalTarget2;
        const secondTriggerValue = selectElement.dataset.conditionalValue2;

        // Get the elements that will be shown/hidden
        const firstConditionalElement = document.getElementById(firstTargetId);
        const secondConditionalElement = secondTargetId ? document.getElementById(secondTargetId) : null;
        // Exit early if the first target element doesn't exist
        if (!firstConditionalElement) {
            return;
        }

        /**
         * Toggles visibility of conditional elements based on select value
         */
        const toggleConditionalElements = () => {
            // Determine if first conditional should be shown
            // If a trigger value is specified, check if it matches the select value
            // Otherwise, show if select has any value
            const shouldShowFirst = firstTriggerValue
                ? firstTriggerValue.split(',').includes(selectElement.value)
                : selectElement.value;

            // Show or hide the first conditional element
            firstConditionalElement.hidden = !shouldShowFirst;

            // Clear any input values when hiding to prevent submitting hidden data
            if (!shouldShowFirst) {
                firstConditionalElement.querySelectorAll('input, textarea').forEach((input) => {
                    // eslint-disable-next-line no-param-reassign
                    input.value = '';
                });
            }
            // Handle second conditional if it exists (e.g., "Other" and "Disabled" on same select)
            if (secondConditionalElement) {
                const shouldShowSecond = secondTriggerValue
                    ? secondTriggerValue.split(',').includes(selectElement.value)
                    : selectElement.value;

                secondConditionalElement.hidden = !shouldShowSecond;

                // Clear inputs when hiding second conditional
                if (!shouldShowSecond) {
                    secondConditionalElement.querySelectorAll('input, textarea').forEach((input) => {
                        // eslint-disable-next-line no-param-reassign
                        input.value = '';
                    });
                }
            }
        };
        // Run immediately on page load to set initial state
        toggleConditionalElements();

        // Run whenever the select value changes
        selectElement.addEventListener('change', toggleConditionalElements);
    });
}
